import { useCallback, useState } from 'react';

/**
 * Owns the open/closed state + target language for `DubGenerateModal`, the
 * kick-off "Generate dub" popover (PLAT-10596 / T8). Mirrors
 * `useTranslationGenerateModalController` so each surface (Transcript &
 * Speakers now; Clip Preview later, T7) shares one wiring.
 *
 * The dub is always scoped to a specific translated language. `open(lang)`
 * starts a first-time generate; `open(lang, { replace: true })` opens the same
 * popover in replace mode (PLAT-10592 / T4) — the caller passes `replace` when a
 * dub already exists for the language, and the modal switches its title,
 * reminder banner, button label, and the `regenerate` flag accordingly.
 * `open(lang, { segmentIds })` opens it in span re-dub mode (PLAT-10616 / T13),
 * scoped to a selection's covered period-level segments.
 * `open(lang, { segmentIds, fillGaps: true })` opens it to fill a clip's gaps:
 * the same scoping, but additive — the named lines are the ones with no audio,
 * and anything already voiced is reused.
 * `open(lang, { segmentIds, firstDub: true })` opens it for a clip's FIRST dub
 * in a language the story has no dub in at all (PLAT-11440): the same clip-sized
 * scoping, but nothing exists to reuse or replace, so the popover presents as
 * the ordinary first generate and the creator chooses each speaker's voice.
 * The modal is mounted only while a language is set.
 */
/** What a caller can ask the popover to open as. */
export interface DubGenerateOpenOptions {
  replace?: boolean;
  segmentIds?: string[];
  fillGaps?: boolean;
  firstDub?: boolean;
}

export function useDubGenerateModalController(): {
  isOpen: boolean;
  targetLanguage: string | null;
  isReplace: boolean;
  segmentIds: string[] | null;
  isFillGaps: boolean;
  isFirstDub: boolean;
  open: (targetLanguage: string, opts?: DubGenerateOpenOptions) => void;
  close: () => void;
} {
  const [targetLanguage, setTargetLanguage] = useState<string | null>(null);
  const [isReplace, setIsReplace] = useState(false);
  const [segmentIds, setSegmentIds] = useState<string[] | null>(null);
  const [isFillGaps, setIsFillGaps] = useState(false);
  const [isFirstDub, setIsFirstDub] = useState(false);

  const open = useCallback((lang: string, opts?: DubGenerateOpenOptions) => {
    setTargetLanguage(lang);
    setIsReplace(opts?.replace ?? false);
    setSegmentIds(opts?.segmentIds ?? null);
    setIsFillGaps(opts?.fillGaps ?? false);
    setIsFirstDub(opts?.firstDub ?? false);
  }, []);
  const close = useCallback(() => {
    setTargetLanguage(null);
    setIsReplace(false);
    setSegmentIds(null);
    setIsFillGaps(false);
    setIsFirstDub(false);
  }, []);

  return {
    isOpen: targetLanguage !== null,
    targetLanguage,
    isReplace,
    segmentIds,
    isFillGaps,
    isFirstDub,
    open,
    close,
  };
}
